import React, { useEffect, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import Cropper from "react-easy-crop";
import Swal from "sweetalert2";
import { PublicUploadLogo } from "../../../services/publicService";

const PublicImageCropModal = ({
  show,
  onClose,
  tenantId,
  onUploadSuccess,
  existingImage = "",
}) => {
  const [selectedImage, setSelectedImage] = useState("");

  const [crop, setCrop] = useState({
    x: 0,
    y: 0,
  });

  const [zoom, setZoom] = useState(1);

  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const [uploading, setUploading] = useState(false);

  // ---------------------------------------------------------
  // RESET WHEN MODAL CLOSES
  // ---------------------------------------------------------

  useEffect(() => {
    if (!show) {
      setSelectedImage("");
      setCrop({
        x: 0,
        y: 0,
      });
      setZoom(1);
      setCroppedAreaPixels(null);
    }
  }, [show]);

  // ---------------------------------------------------------
  // SELECT IMAGE
  // ---------------------------------------------------------

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    if (!file.type.startsWith("image/")) {
      Swal.fire("Error!", "Please select a valid image.", "error");

      e.target.value = "";
      return;
    }

    const maxSize = 10 * 1024 * 1024;

    if (file.size > maxSize) {
      Swal.fire(
        "Error!",
        "Image size is too large. Please select an image smaller than 10 MB.",
        "error",
      );

      e.target.value = "";
      return;
    }

    const imageUrl = URL.createObjectURL(file);

    // This image exists only inside modal
    setSelectedImage(imageUrl);

    setCrop({
      x: 0,
      y: 0,
    });

    setZoom(1);

    setCroppedAreaPixels(null);

    e.target.value = "";
  };

  // ---------------------------------------------------------
  // CROP COMPLETE
  // ---------------------------------------------------------

  const handleCropComplete = (_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  };

  // ---------------------------------------------------------
  // CREATE CROPPED IMAGE
  // ---------------------------------------------------------

  const createCroppedImage = async (imageSrc, pixelCrop) => {
    const image = new Image();

    image.src = imageSrc;

    await new Promise((resolve, reject) => {
      image.onload = resolve;
      image.onerror = reject;
    });

    // Final image size
    const OUTPUT_SIZE = 800;

    const canvas = document.createElement("canvas");

    canvas.width = OUTPUT_SIZE;
    canvas.height = OUTPUT_SIZE;

    const ctx = canvas.getContext("2d");

    if (!ctx) {
      throw new Error("Could not create canvas");
    }

    // Draw cropped image
    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      OUTPUT_SIZE,
      OUTPUT_SIZE,
    );

    const blob = await new Promise((resolve) => {
      canvas.toBlob(resolve, "image/png", 1);
    });

    if (!blob) {
      throw new Error("Could not create cropped image");
    }

    return new File([blob], `academy-logo-${Date.now()}.png`, {
      type: "image/png",
    });
  };

  // ---------------------------------------------------------
  // UPLOAD
  // ---------------------------------------------------------
  const handleSave = async () => {
    if (!selectedImage) {
      return;
    }

    if (!croppedAreaPixels) {
      Swal.fire("Error!", "Please crop the image.", "error");
      return;
    }

    try {
      setUploading(true);

      // Crop first
      const croppedFile = await createCroppedImage(
        selectedImage,
        croppedAreaPixels,
      );

      // Upload cropped image
      const response = await PublicUploadLogo(croppedFile);

      // Send uploaded response to parent
      if (onUploadSuccess) {
        onUploadSuccess(response);
      }

      // Close modal
      handleClose();
    } catch (error) {
      console.error("Image upload failed:", error);

      const message =
        error?.response?.data?.detail ||
        error?.response?.data?.errorMessage ||
        error?.message ||
        "Image upload failed.";

      Swal.fire("Error!", message, "error");
    } finally {
      setUploading(false);
    }
  };

  // ---------------------------------------------------------
  // CLOSE
  // ---------------------------------------------------------

  const handleClose = () => {
    if (selectedImage) {
      URL.revokeObjectURL(selectedImage);
    }

    setSelectedImage("");

    setCrop({
      x: 0,
      y: 0,
    });

    setZoom(1);

    setCroppedAreaPixels(null);

    onClose();
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      backdrop="static"
      keyboard={!uploading}
      dialogClassName="academy-logo-modal"
    >
      <Modal.Header
        closeButton={!uploading}
        style={{
          padding: "12px 16px",
          borderBottom: "1px solid #eee",
        }}
      >
        <div>
          <Modal.Title
            style={{
              fontSize: "16px",
              fontWeight: 600,
            }}
          >
            Academy Logo
          </Modal.Title>

          <div
            className="text-muted"
            style={{
              fontSize: "11px",
              marginTop: "2px",
            }}
          >
            Crop your logo before uploading
          </div>
        </div>
      </Modal.Header>

      <Modal.Body
        style={{
          padding: "16px",
          overflow: "hidden",
        }}
      >
        {!selectedImage ? (
          /* ================================
         UPLOAD STATE
      ================================= */
          <div
            style={{
              height: "180px",
              border: "1px dashed #cfd4da",
              borderRadius: "10px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              background: "#fafafa",
            }}
          >
            <div>
              <div
                style={{
                  width: "44px",
                  height: "44px",
                  borderRadius: "50%",
                  background: "#eef4ff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 8px",
                }}
              >
                <i
                  className="fa fa-cloud-upload"
                  style={{
                    fontSize: "18px",
                  }}
                />
              </div>

              <div
                style={{
                  fontSize: "13px",
                  fontWeight: 500,
                }}
              >
                Select academy logo
              </div>

              <div
                className="text-muted"
                style={{
                  fontSize: "11px",
                  marginTop: "3px",
                }}
              >
                JPG, PNG or WEBP • Max 10 MB
              </div>

              <label
                htmlFor="academy-logo-input"
                className="btn btn-primary btn-sm mt-2"
                style={{
                  cursor: "pointer",
                  fontSize: "12px",
                }}
              >
                Select Image
              </label>

              <input
                id="academy-logo-input"
                type="file"
                hidden
                accept="image/png,image/jpeg,image/jpg,image/webp"
                onChange={handleFileChange}
                disabled={uploading}
              />
            </div>
          </div>
        ) : (
          /* ================================
         CROP STATE
      ================================= */
          <>
            <div
              style={{
                position: "relative",
                width: "100%",
                height: "330px",
                background: "#181818",
                borderRadius: "8px",
                overflow: "hidden",
              }}
            >
              <Cropper
                image={selectedImage}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="rect"
                showGrid={true}
                onCropChange={setCrop}
                onCropComplete={handleCropComplete}
                onZoomChange={setZoom}
              />
            </div>

            <div className="mt-3">
              <div
                className="d-flex justify-content-between align-items-center"
                style={{
                  fontSize: "12px",
                  marginBottom: "4px",
                }}
              >
                <span className="fw-semibold">Zoom</span>

                <span className="text-muted">{zoom.toFixed(1)}x</span>
              </div>

              <input
                type="range"
                className="form-range"
                min="1"
                max="3"
                step="0.1"
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                disabled={uploading}
              />
            </div>

            <div
              className="text-muted text-center"
              style={{
                fontSize: "10px",
                marginTop: "2px",
              }}
            >
              Square logo • Final size 800 × 800 px
            </div>
          </>
        )}
      </Modal.Body>

      <Modal.Footer
        style={{
          padding: "10px 16px",
          borderTop: "1px solid #eee",
        }}
      >
        <Button
          variant="light"
          size="sm"
          onClick={handleClose}
          disabled={uploading}
        >
          Cancel
        </Button>

        {selectedImage && (
          <Button
            variant="primary"
            size="sm"
            onClick={handleSave}
            disabled={uploading || !croppedAreaPixels}
            style={{
              minWidth: "100px",
            }}
          >
            {uploading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-1"
                  role="status"
                />
                Uploading
              </>
            ) : (
              <>
                <i className="fa fa-check me-1" />
                Crop & Upload
              </>
            )}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default PublicImageCropModal;
